import { Link, useSearchParams } from 'react-router-dom'
import { api, useApi } from '../api/queries'
import type { DriverStats, F2Champion, F2Season } from '../api/types'
import { DriverStatsTable, NameFilter } from '../components/CareerTables'
import { DataState } from '../components/DataState'
import type { Removals } from '../chart/SeasonProgression'
import { useUniversePath } from '../lib/universe'
import { UniverseConstructorPage, UniverseDriverPage, type Universe } from './PeoplePages'
import { UniverseRacePage } from './RacePage'
import { Drivers, Standings } from './SeasonPage'

/**
 * The three rules replayed over the whole history and stored by `f1cal f2 build`:
 * Formula 2's, where a champion never returns, Formula 1.5's, where each
 * season's fastest teams are left out, and Reverse's, where the last classified
 * wins. Same pages, same payloads.
 */
type Rule = 'f2' | 'f15' | 'reverse'

const REMOVED_NOTE =
  'A removed driver’s pole passes to the next qualifier; their fastest lap is not re-awarded.'

const RULES = {
  f2: {
    name: 'Formula 2’s rule',
    prose:
      'Formula 2’s rule: the champion moves up and does not return. Here every Formula One ' +
      'champion is barred from all the seasons after their title, and the field behind them ' +
      'moves up race by race.',
    note: REMOVED_NOTE,
    removed: 'Removed, as earlier champions: ',
    none: 'No earlier champion raced this season.',
    query: {},
    champions: () => api.GET('/api/v1/f2/champions', {}),
    drivers: () => api.GET('/api/v1/f2/drivers', {}),
    season: (year: number) =>
      api.GET('/api/v1/f2/seasons/{year}', { params: { path: { year } } }),
    driverSeasons: (slug: string) =>
      api.GET('/api/v1/f2/drivers/{slug}/seasons', { params: { path: { slug } } }),
  },
  f15: {
    name: 'Formula 1.5’s rule',
    prose:
      'Formula 1.5, the best of the rest: in every season, each team with a driver on the ' +
      'podium in at least a quarter of its Grands Prix is left out, cars and all, and the ' +
      'field behind moves up race by race. That is how the r/Formula1Point5 community draws ' +
      'its grids for past seasons. Teams here are constructors: a front-runner’s privateer ' +
      'cars go with its works team.',
    note: REMOVED_NOTE,
    removed: 'Removed, as the fastest teams: ',
    none: 'No team was fast enough to be removed.',
    query: {},
    champions: () => api.GET('/api/v1/f15/champions', {}),
    drivers: () => api.GET('/api/v1/f15/drivers', {}),
    season: (year: number) =>
      api.GET('/api/v1/f15/seasons/{year}', { params: { path: { year } } }),
    driverSeasons: (slug: string) =>
      api.GET('/api/v1/f15/drivers/{slug}/seasons', { params: { path: { slug } } }),
  },
  reverse: {
    name: 'the Reverse rule',
    prose:
      'Reverse: every finishing order is turned upside down. The last classified finisher ' +
      'wins and scores a winner’s points, under the season’s own points table and its own ' +
      'rule on which results count, and so on up the field; a driver who is not classified ' +
      'scores nothing. Sprints are turned over too.',
    note:
      'The pole goes to the last qualifier. Nobody has the fastest lap: its mirror, the ' +
      'slowest, is not in the record, so its point is not awarded.',
    removed: '',
    none: 'Nobody is removed: the last of the classified finishers wins every race.',
    query: { reverse: true },
    champions: () => api.GET('/api/v1/reverse/champions', {}),
    drivers: () => api.GET('/api/v1/reverse/drivers', {}),
    season: (year: number) =>
      api.GET('/api/v1/reverse/seasons/{year}', { params: { path: { year } } }),
    driverSeasons: (slug: string) =>
      api.GET('/api/v1/reverse/drivers/{slug}/seasons', { params: { path: { slug } } }),
  },
} as const

function useChampions(rule: Rule) {
  return useApi<F2Champion[]>([rule, 'champions'], RULES[rule].champions)
}

function Prose({ rule }: { rule: Rule }) {
  return (
    <p className="prose muted">
      {RULES[rule].prose} {RULES[rule].note}
    </p>
  )
}

function ChampionshipPage({ rule }: { rule: Rule }) {
  const champions = useChampions(rule)

  return (
    <section>
      <h1>Champions under {RULES[rule].name}</h1>
      <Prose rule={rule} />
      <DataState query={champions} empty="No seasons.">
        {(rows) => (
          <div className="table-scroll">
            <table className="grid">
              <thead>
                <tr>
                  <th scope="col">Year</th>
                  <th scope="col">Drivers’ champion</th>
                  <th scope="col">Constructors’ champion</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.year}>
                    <td>
                      <Link to={`/${rule}/season?y=${row.year}`}>{row.year}</Link>
                    </td>
                    {row.complete ? (
                      <>
                        <td>
                          <Named entity={row.f2_driver} path="drivers" />
                        </td>
                        <td>
                          <Named entity={row.f2_constructor} path="constructors" />
                        </td>
                      </>
                    ) : (
                      <td colSpan={2} className="muted">
                        In progress
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DataState>
    </section>
  )
}

/** A champion's name, or a dash where the title did not exist yet (before 1958). */
function Named({
  entity,
  path,
}: {
  entity: { slug: string; name: string } | null | undefined
  path: 'drivers' | 'constructors'
}) {
  const to = useUniversePath()
  if (!entity) return <span className="muted">—</span>
  return <Link to={to(`/${path}/${entity.slug}`)}>{entity.name}</Link>
}

function DriversPage({ rule }: { rule: Rule }) {
  const drivers = useApi<DriverStats[]>([rule, 'drivers'], RULES[rule].drivers)
  return (
    <section>
      <h1>Drivers under {RULES[rule].name}</h1>
      <Prose rule={rule} />
      <NameFilter label="Search drivers" />
      <DataState query={drivers} empty="No drivers.">
        {(rows) => <DriverStatsTable rows={rows} />}
      </DataState>
    </section>
  )
}

const TABS = [
  { key: 'drivers', label: 'Drivers' },
  { key: 'constructors', label: 'Constructors' },
] as const

/**
 * One season under the rule, drawn exactly as `/seasons/:year` draws it: the
 * same chart, results matrix and table, replayed without whom the rule removed.
 */
function SeasonPage({ rule }: { rule: Rule }) {
  const [params, setParams] = useSearchParams()
  const champions = useChampions(rule)
  const years = (champions.data ?? []).map((row) => row.year)
  const asked = Number(params.get('y'))
  const year = years.includes(asked) ? asked : years[0]
  const tab = params.get('tab') === 'constructors' ? 'constructors' : 'drivers'
  const season = useApi<F2Season>([rule, 'season', year], () => RULES[rule].season(year ?? 0), {
    enabled: year !== undefined,
  })
  const to = useUniversePath()
  // Pins name one championship's competitors, so they do not survive a switch.
  const choose = (next: { y?: string; tab?: string }) => {
    const merged = { y: params.get('y') ?? '', tab: params.get('tab') ?? '', ...next }
    setParams(
      Object.fromEntries(
        Object.entries(merged).filter(([key, value]) => value && key !== 'pin'),
      ),
      { replace: true },
    )
  }

  return (
    <section>
      <h1>A season under {RULES[rule].name}</h1>
      <label className="search">
        <span className="muted">Season</span>
        <select value={year ?? ''} onChange={(event) => choose({ y: event.target.value })}>
          {years.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>
      <DataState query={season}>
        {(data) => {
          const removals = removalsOf(rule, data)
          const removed = [
            ...data.barred.map((driver) => ({ ...driver, path: 'drivers' })),
            ...(data.barred_constructors ?? []).map((team) => ({
              ...team,
              path: 'constructors',
            })),
          ]
          return (
            <>
              <p className="prose">
                {removed.length ? (
                  <>
                    <span className="muted">{RULES[rule].removed}</span>
                    {removed.map((entity, index) => (
                      <span key={entity.slug}>
                        {index > 0 ? ', ' : ''}
                        <Link to={to(`/${entity.path}/${entity.slug}`)}>{entity.name}</Link>
                      </span>
                    ))}
                    .
                  </>
                ) : (
                  <span className="muted">{RULES[rule].none}</span>
                )}{' '}
                <Link to={`/seasons/${data.year}`}>The season as it happened →</Link>
              </p>
              <nav className="tabs">
                {TABS.map((entry) => (
                  <button
                    key={entry.key}
                    type="button"
                    className={entry.key === tab ? 'tabs__tab tabs__tab--on' : 'tabs__tab'}
                    aria-current={entry.key === tab}
                    onClick={() => choose({ tab: entry.key === 'drivers' ? '' : entry.key })}
                  >
                    {entry.label}
                  </button>
                ))}
              </nav>
              {tab === 'drivers' ? (
                <Drivers year={data.year} removals={removals} />
              ) : (
                <Standings year={data.year} championship="constructors" removals={removals} />
              )}
            </>
          )
        }}
      </DataState>
    </section>
  )
}

/** What the rule did to one season, as the season routes take it: whom it removed. */
function removalsOf(rule: Rule, season: F2Season): Removals {
  const drivers = season.barred.map((driver) => driver.slug)
  const teams = (season.barred_constructors ?? []).map((team) => team.slug)
  return {
    ...RULES[rule].query,
    ...(drivers.length ? { exclude_drivers: drivers.join(',') } : {}),
    ...(teams.length ? { exclude_constructors: teams.join(',') } : {}),
  }
}

function universe(rule: Rule): Universe {
  return {
    path: `/${rule}`,
    suffix: `under ${RULES[rule].name}`,
    use: function useRule() {
      return {
        chosen: true,
        search: '',
        summary: <Prose rule={rule} />,
        careers: useApi<DriverStats[]>([rule, 'drivers'], RULES[rule].drivers),
        driverSeasons: (slug) => [
          [rule, 'driver-seasons', slug],
          () => RULES[rule].driverSeasons(slug),
        ],
      }
    },
    useRemovals: function useRuleRemovals(year) {
      const season = useApi<F2Season>([rule, 'season', year], () => RULES[rule].season(year))
      return season.data ? removalsOf(rule, season.data) : undefined
    },
  }
}

const F2 = universe('f2')
const F15 = universe('f15')
const REVERSE = universe('reverse')

export const F2ChampionshipPage = () => <ChampionshipPage rule="f2" />
export const F2DriversPage = () => <DriversPage rule="f2" />
export const F2SeasonPage = () => <SeasonPage rule="f2" />
export const F2DriverPage = () => <UniverseDriverPage universe={F2} />
export const F2ConstructorPage = () => <UniverseConstructorPage universe={F2} />
export const F2RacePage = () => <UniverseRacePage universe={F2} />
export const F15ChampionshipPage = () => <ChampionshipPage rule="f15" />
export const F15DriversPage = () => <DriversPage rule="f15" />
export const F15SeasonPage = () => <SeasonPage rule="f15" />
export const F15DriverPage = () => <UniverseDriverPage universe={F15} />
export const F15ConstructorPage = () => <UniverseConstructorPage universe={F15} />
export const F15RacePage = () => <UniverseRacePage universe={F15} />
export const ReverseChampionshipPage = () => <ChampionshipPage rule="reverse" />
export const ReverseDriversPage = () => <DriversPage rule="reverse" />
export const ReverseSeasonPage = () => <SeasonPage rule="reverse" />
export const ReverseDriverPage = () => <UniverseDriverPage universe={REVERSE} />
export const ReverseConstructorPage = () => <UniverseConstructorPage universe={REVERSE} />
export const ReverseRacePage = () => <UniverseRacePage universe={REVERSE} />
